<template>
  <div class="bg-faint-grey content-height content-top-bottom">
    <v-subheader :goBackButtonTo="{ name: 'myPills' }" :title="$t('page.order.add.new.pill')">
      <i class="icon-export"></i>
      <a @click.prevent="savePill">{{$t("page.order.add.new.save")}}</a>
    </v-subheader>
    <div id="add-pill-form" class="form-pill container">
      <div class="form-pill__top form-pill-top">
        <div class="form-pill-top__item item1" id="pzn8-number-container">
          <label class="label-form">{{ currentMedKeyTypeName }}</label>
          <v-input
            id="pzn-number-input"
            v-model="form.medKey"
            @blur="$v.form.medKey.$touch"
            block
            :has-error="hasFieldError('medKey')"
          ></v-input>
          <div v-if="hasFieldError('medKey')" class="error-label">{{ $t("common.required")}}</div>
        </div>
        <div id="pzn8-medicine-name-container" class="form-pill-top__item item2">
          <label class="label-form">{{ $t("page.order.list.header.medicinename") }}</label>
          <v-input
            block
            v-model="form.medicineName"
            @blur="$v.form.medicineName.$touch"
            :placeholder="$t('common.typehere')"
            :has-error="hasFieldError('medicineName')"
          />
          <div v-if="hasFieldError('medicineName')" class="error-label">{{ $t("common.required") }}</div>
        </div>
        <div class="form-pill-top__item item3">
          <label class="label-form">{{ $t("page.order.list.header.manufacturer")}}</label>
          <v-search-select
            v-model="form.manufacturer"
            :options="manufacturers"
            :search-phrase="form.manufacturer"
            @input="$v.form.manufacturer.$touch"
          />
          <div v-if="hasFieldError('manufacturer')" class="error-label">{{ $t("common.required") }}</div>
        </div>
        <div class="form-pill-top__item item4">
          <label>
            <v-checkbox type="checkbox" v-model="form.uvSensibility"></v-checkbox>
            <span>UV Sensibility</span>
          </label>
        </div>
      </div>

      <v-pill-shape-form @update="({ pill }) => $set(form, 'pill', pill)" :pill-form-data="form" />
    </div>
    <v-error-popup
      v-if="saveErrorMessage"
      :description="saveErrorMessage"
      :submit-button-text="saveErrorButtonText"
      @close="saveErrorMessage = null"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { required } from "vuelidate/lib/validators";
import VSubheader from "@/components/v-subheader";
import { VInput, VSearchSelect, VCheckbox } from "shared";
import VPillShapeForm from "@/components/pills/v-pill-shape-form";
import VErrorPopup from "@/components/v-error-popup";
import * as ManufacturersDAO from "@/dao/manufacturers-dao";
import * as PillsDAO from "@/dao/pills-dao";

export default {
  name: "PAddPill",
  props: {
    searchedMedKeyType: {
      type: String,
    },
  },
  components: {
    VSubheader,
    VInput,
    VSearchSelect,
    VCheckbox,
    VPillShapeForm,
    VErrorPopup,
  },
  async created() {
    this.manufacturers = await ManufacturersDAO.getManufacturers();
    this.form.medKey = this.searchedMedKeyType;
  },
  data() {
    return {
      form: {
        manufacturer: "",
        medicineName: "",
        uvSensibility: false,
        pill: {},
        medKey: "",
      },
      manufacturers: [],
      saveErrorMessage: null,
      saveErrorButtonText: "",
    };
  },
  methods: {
    hasFieldError(field) {
      return this.$v.form[field].$dirty && this.$v.form[field].$invalid && !this.$v.form[field].required;
    },
    async savePill() {
      this.$v.form.$touch();
      if (this.$v.form.manufacturer.$invalid || this.$v.form.medicineName.$invalid) {
        return;
      }

      const { manufacturer, ...data } = this.form;
      const item = this.manufacturers.find((m) => m.name === manufacturer);
      if (item) {
        data.manufacturerId = item.key;
      } else {
        data.manufacturerName = manufacturer;
      }

      try {
        await PillsDAO.addPill(this.currentWorkListId, data);
        this.$router.push({ name: "myPills" });
      } catch (error) {
        this.saveErrorMessage = this.$t(error.response.data.errorTranslationKey);

        if (error.response.status === 409) {
          this.saveErrorButtonText = "add-pill.button.return-to-bag";
        } else {
          this.saveErrorButtonText = "";
        }
      }
    },
  },
  computed: {
    ...mapGetters("workLists", ["currentWorkListId"]),
    ...mapGetters("medKey", ["currentMedKeyTypeName"]),
  },
  validations: {
    form: {
      medKey: {
        required,
      },
      medicineName: {
        required,
      },
      manufacturer: {
        required,
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/variables";

.form-pill-top {
  display: flex;
  flex-wrap: wrap;
  padding: 19px 0 24px 39px;
  margin-top: 90px;
  background: $color-white;
  border: 1px solid $color-grey;
  &__item {
    .label-form {
      font-weight: bold;
      font-size: 13px;
      line-height: 14px;
      letter-spacing: 0.433333px;
      color: #999999;
      display: block;
      margin: 3px 0 7px 0;
    }
    &::v-deep .input[type="text"] {
      background: #fdfdfd;
      height: 40px;
      width: 100%;
    }
    &.item1 {
      width: 240px;
      margin-right: 8px;
    }
    &.item2 {
      width: 447px;
      margin-right: 8px;
    }
    &.item3 {
      width: 263px;
      position: relative;
    }
    &.item4 {
      margin: 36px 0 0 35px;
      label {
        display: flex;
        align-items: center;
        span {
          font-weight: bold;
          font-size: 13px;
          line-height: 14px;
          letter-spacing: 0.433333px;
          color: #333333;
          padding-left: 11px;
          cursor: pointer;
        }
      }
    }
  }
}
.error-label {
  color: $color-red;
  font-size: 12px;
  margin: 5px 0 -18px 11px;
  text-transform: capitalize;
}
</style>
